import axios from 'axios';

// Use localhost for development - make this configurable
const API_HOST = process.env.REACT_APP_API_HOST || 'voicebotdemo.asttecs.com'; // Update this to your server's hostname
const API_PORT = process.env.REACT_APP_API_PORT || '3001';
const API_BASE = `http://${API_HOST}:${API_PORT}/api`;

class FileService {
  constructor() {
    // Base URL would point to your backend in production
    this.baseUrl = API_BASE;
    this.rasaUrl = `${API_BASE}/rasa`;
    console.log('FileService initialized with API base:', this.baseUrl);

    // Configure axios timeout and headers
    axios.defaults.timeout = 30000; // 30 seconds
    delete axios.defaults.headers.common['Content-Type']; // Let axios set the correct content type

    // Set initial connection state
    this.isServerConnected = false;

    // Initialize slots storage
    this.rasaSlots = [];
  }

  // Test connection to server with retry
  async testConnection(retryCount = 0) {
    try {
      console.log('Testing connection to API server...');
      console.log('Attempting to connect to:', this.baseUrl);

      // Try the test endpoint first
      try {
        const testResponse = await axios.get(`${this.baseUrl}/test`);
        console.log('Test endpoint response:', testResponse.data);
      } catch (testError) {
        console.warn('Test endpoint failed, trying HEAD request:', testError.message);
      }

      // Fallback to HEAD request
      const baseServerUrl = this.baseUrl.replace('/api', '');
      const response = await axios.head(baseServerUrl);
      console.log('✅ Connection to API server successful!', response.status);
      this.isServerConnected = true;
      return true;
    } catch (error) {
      console.error('❌ Connection to API server failed:', error.message);
      console.error('Failed connecting to:', this.baseUrl);
      console.error('Full error:', error);

      // Get more specific error information
      let errorDetails = 'Unknown error';
      if (error.code === 'ECONNREFUSED') {
        errorDetails = `Connection refused to ${this.baseUrl}. Is the server running on port ${API_PORT}?`;
      } else if (error.code === 'ETIMEDOUT') {
        errorDetails = 'Connection timed out';
      } else if (error.response) {
        errorDetails = `Server responded with status ${error.response.status}`;
      } else if (error.request) {
        errorDetails = 'No response received from server';
      } else {
        errorDetails = error.message;
      }

      console.warn(`Server connection error: ${errorDetails}`);

      // Retry once after a delay if this is the first attempt
      if (retryCount === 0) {
        console.log('Retrying connection in 2 seconds...');
        setTimeout(() => this.testConnection(1), 2000);
      }

      this.isServerConnected = false;
      return false;
    }
  }

  // Check server connection before making requests
  async ensureServerConnection() {
    if (!this.isServerConnected) {
      // Try to connect again
      const isConnected = await this.testConnection();
      if (!isConnected) {
        throw new Error('Server is not running. Please start the server with: node backend/server.js');
      }
    }
  }

  // Get list of available flows from FLOWS directory
  async getFlows() {
    try {
      await this.ensureServerConnection();
      console.log('Fetching flows from FLOWS directory:', `${this.baseUrl}/flows`);
      const response = await axios.get(`${this.baseUrl}/flows`);
      console.log('Flows response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching flows:', error);

      // Add more detailed error information
      let errorMsg = 'Failed to fetch flows';

      if (error.response) {
        // Server responded with error
        errorMsg = `Server error ${error.response.status}: ${error.response.data}`;
      } else if (error.request) {
        // Request made but no response received
        errorMsg = `Network error: No response from server. Is the server running at ${this.baseUrl.replace('/api', '')}?`;
        console.warn('Try starting the server with: node backend/server.js');
      } else {
        // Request setup error
        errorMsg = `Request error: ${error.message}`;
      }

      throw new Error(errorMsg);
    }
  }

  // Get list of unique organisations
  async getOrganisations() {
    try {
      await this.ensureServerConnection();
      const response = await axios.get(`${this.baseUrl}/organisations`);
      return response.data;
    } catch (error) {
      console.error('Error fetching organisations:', error);
      throw new Error(`Failed to fetch organisations: ${error.message}`);
    }
  }

  // Get a specific flow by filename from FLOWS directory
  async getFlow(filename) {
    try {
      console.log('Fetching flow from FLOWS directory:', filename);
      const response = await axios.get(`${this.baseUrl}/flows/${filename}`);
      console.log('Flow data retrieved successfully');
      return response.data;
    } catch (error) {
      console.error(`Error fetching flow ${filename}:`, error);

      // Add more detailed error information
      let errorMsg = `Failed to fetch flow ${filename}`;

      if (error.response) {
        errorMsg = `Server error ${error.response.status}: ${error.response.data}`;
      } else if (error.request) {
        errorMsg = `Network error: No response from server`;
      } else {
        errorMsg = `Request error: ${error.message}`;
      }

      throw new Error(errorMsg);
    }
  }

  // Add method to get RASA slots
  getRasaSlots() {
    return this.rasaSlots || [];
  }

  // Load RASA data for specific user (merges default, organisation, and custom data)
  async loadRasaData(userId) {
    try {
      console.log('FileService: Loading merged RASA data for user:', userId);

      // 1. Fetch Default (admin, status 0)
      const defaultIntents = await this.getIntents('admin', 0);
      const defaultSlots = await this.getSlots('admin', 0);

      // 2. Fetch Organisation (userId, status 1)
      const orgIntents = await this.getIntents(userId, 1);
      const orgSlots = await this.getSlots(userId, 1);

      // 3. Fetch My Intent (userId, status 0)
      const myIntents = await this.getIntents(userId, 0);
      const mySlots = await this.getSlots(userId, 0);

      // Merge and deduplicate intents by name
      const allIntents = [...defaultIntents, ...orgIntents, ...myIntents];
      const uniqueIntents = Array.from(new Map(allIntents.map(item => [item.intent, item])).values());

      // Merge and deduplicate slots by name
      // We also need to fetch questions for each slot to ensure they are available in PropertyPanel
      const allSlots = [...defaultSlots, ...orgSlots, ...mySlots];
      const uniqueSlotsMap = new Map(allSlots.map(item => [item.slot, item]));
      const uniqueSlots = Array.from(uniqueSlotsMap.values());

      // Fetch questions for all unique slots
      const slotsWithQuestions = await Promise.all(uniqueSlots.map(async (slot) => {
        try {
          const questions = await this.getSlotQuestions(slot.id);
          return {
            ...slot,
            questions: questions.map(q => q.slot_questions)
          };
        } catch (e) {
          console.warn(`Failed to fetch questions for slot ${slot.slot}:`, e);
          return { ...slot, questions: [] };
        }
      }));

      console.log(`FileService: Loaded ${uniqueIntents.length} intents and ${slotsWithQuestions.length} slots.`);
      return {
        success: true,
        intents: uniqueIntents,
        slots: slotsWithQuestions
      };
    } catch (error) {
      console.error('Error loading RASA data:', error);
      throw new Error(`Failed to load RASA data: ${error.message}`);
    }
  }

  // Get intents for specific user and status
  async getIntents(userId, status = 0) {
    try {
      const response = await axios.get(`${this.rasaUrl}/intents?organisation_id=${userId}&status=${status}`);
      return response.data;
    } catch (error) {
      console.error(`Error loading intents for ${userId} (status ${status}):`, error);
      return []; // Return empty array on error to allow merging to continue
    }
  }

  // Get intent examples for specific intent
  async getIntentExamples(intentId) {
    try {
      const response = await axios.get(`${this.rasaUrl}/examples?intent_id=${intentId}`);
      return response.data;
    } catch (error) {
      console.error('Error loading intent examples:', error);
      throw new Error(`Failed to load intent examples: ${error.message}`);
    }
  }

  // Get slots for specific user and status
  async getSlots(userId, status = 0) {
    try {
      const response = await axios.get(`${this.rasaUrl}/slots?organisation_id=${userId}&status=${status}`);
      return response.data;
    } catch (error) {
      console.error(`Error loading slots for ${userId} (status ${status}):`, error);
      return []; // Return empty array on error
    }
  }

  // Get slot questions for specific slot
  async getSlotQuestions(slotId) {
    try {
      const response = await axios.get(`${this.rasaUrl}/questions?slot_id=${slotId}`);
      return response.data;
    } catch (error) {
      console.error('Error loading slot questions:', error);
      return [];
    }
  }

  // Add new intent
  async addIntent(userId, intent, status = 0) {
    try {
      const response = await axios.post(`${this.rasaUrl}/intents`, {
        user_id: userId,
        intent: intent,
        status: status
      });
      return response.data;
    } catch (error) {
      console.error('Error adding intent:', error);
      throw new Error(`Failed to add intent: ${error.message}`);
    }
  }

  // Update intent
  async updateIntent(id, intent) {
    try {
      const response = await axios.put(`${this.rasaUrl}/intents/${id}`, {
        intent: intent
      });
      return response.data;
    } catch (error) {
      console.error('Error updating intent:', error);
      throw new Error(`Failed to update intent: ${error.message}`);
    }
  }

  // Delete intent
  async deleteIntent(id) {
    try {
      const response = await axios.delete(`${this.rasaUrl}/intents/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting intent:', error);
      throw new Error(`Failed to delete intent: ${error.message}`);
    }
  }

  // Add intent example
  async addIntentExample(userId, intentId, example, status = 0) {
    try {
      const response = await axios.post(`${this.rasaUrl}/examples`, {
        user_id: userId,
        intent_id: intentId,
        intent_examples: example,
        status: status
      });
      return response.data;
    } catch (error) {
      console.error('Error adding example:', error);
      throw new Error(`Failed to add example: ${error.message}`);
    }
  }

  // Update intent example
  async updateIntentExample(id, example) {
    try {
      const response = await axios.put(`${this.rasaUrl}/examples/${id}`, {
        intent_examples: example
      });
      return response.data;
    } catch (error) {
      console.error('Error updating example:', error);
      throw new Error(`Failed to update example: ${error.message}`);
    }
  }

  // Delete intent example
  async deleteIntentExample(id) {
    try {
      const response = await axios.delete(`${this.rasaUrl}/examples/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting example:', error);
      throw new Error(`Failed to delete example: ${error.message}`);
    }
  }

  // Add new slot
  async addSlot(userId, slotName, slotType, status = 0) {
    try {
      const response = await axios.post(`${this.rasaUrl}/slots`, {
        user_id: userId,
        slot: slotName,
        slot_type: slotType,
        status: status
      });
      return response.data;
    } catch (error) {
      console.error('Error adding slot:', error);
      throw new Error(`Failed to add slot: ${error.message}`);
    }
  }

  // Update slot
  async updateSlot(id, slotName, slotType) {
    try {
      const response = await axios.put(`${this.rasaUrl}/slots/${id}`, {
        slot: slotName,
        slot_type: slotType
      });
      return response.data;
    } catch (error) {
      console.error('Error updating slot:', error);
      throw new Error(`Failed to update slot: ${error.message}`);
    }
  }

  // Delete slot
  async deleteSlot(id) {
    try {
      const response = await axios.delete(`${this.rasaUrl}/slots/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting slot:', error);
      throw new Error(`Failed to delete slot: ${error.message}`);
    }
  }

  // Add slot question
  async addSlotQuestion(userId, slotId, question, status = 0) {
    try {
      const response = await axios.post(`${this.rasaUrl}/questions`, {
        user_id: userId,
        slot_id: slotId,
        slot_questions: question,
        status: status
      });
      return response.data;
    } catch (error) {
      console.error('Error adding question:', error);
      throw new Error(`Failed to add question: ${error.message}`);
    }
  }

  // Update slot question
  async updateSlotQuestion(id, question) {
    try {
      const response = await axios.put(`${this.rasaUrl}/questions/${id}`, {
        slot_questions: question
      });
      return response.data;
    } catch (error) {
      console.error('Error updating question:', error);
      throw new Error(`Failed to update question: ${error.message}`);
    }
  }

  // Delete slot question
  async deleteSlotQuestion(id) {
    try {
      const response = await axios.delete(`${this.rasaUrl}/questions/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting question:', error);
      throw new Error(`Failed to delete question: ${error.message}`);
    }
  }

  // Trigger RASA training
  async trainRasa(userId, status = 0) {
    try {
      const response = await axios.post(`${this.rasaUrl}/train`, {
        user_id: userId,
        status: status
      });
      return response.data;
    } catch (error) {
      console.error('Error training RASA:', error);
      throw new Error(`Failed to train RASA: ${error.message}`);
    }
  }

  // Get RASA training logs
  async getRasaLogs(userId, status = 0) {
    try {
      const response = await axios.get(`${this.rasaUrl}/logs/${userId}/${status}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching RASA logs:', error);
      return { logs: [] };
    }
  }

  // Get user flows (Simplified for new system)
  async getUserFlows(userId) {
    try {
      const response = await axios.get(`${this.baseUrl}/flows/${userId}`);
      return response.data;
    } catch (error) {
      console.error('Error loading user flows:', error);
      throw new Error(`Failed to load user flows: ${error.message}`);
    }
  }

  // Get intents for a specific flow (Simplified for new system)
  async getIntentsForFlow(userId, status = 0) {
    return this.getIntents(userId, status);
  }

  // Copy organisation data
  async copyOrganisationData(sourceOrgId, targetOrgId, status = 0) {
    try {
      const response = await axios.post(`${this.rasaUrl}/copy-organisation`, {
        source_org_id: sourceOrgId,
        target_org_id: targetOrgId,
        status: status
      });
      return response.data;
    } catch (error) {
      console.error('Error copying organisation data:', error);
      throw new Error(`Failed to copy organisation data: ${error.message}`);
    }
  }

  // Save RASA data and generate file
  async saveRasaData(userId, status = 0) {
    try {
      const response = await axios.post(`${this.rasaUrl}/save`, {
        user_id: userId,
        status: status
      });
      return response.data;
    } catch (error) {
      console.error('Error saving RASA data:', error);
      throw new Error(`Failed to save RASA data: ${error.message}`);
    }
  }

  // Parse CSV file for examples
  parseCSVForExamples(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const csv = e.target.result;
          const lines = csv.split('\n');
          const headers = lines[0].split(',').map(h => h.trim().toLowerCase());

          // Check if 'examples' column exists
          const examplesIndex = headers.findIndex(h => h === 'examples');
          if (examplesIndex === -1) {
            reject(new Error('CSV must have an "examples" column'));
            return;
          }

          const examples = [];
          for (let i = 1; i < lines.length; i++) {
            const line = lines[i].trim();
            if (line) {
              const values = line.split(',').map(v => v.trim());
              if (values[examplesIndex]) {
                examples.push(values[examplesIndex]);
              }
            }
          }

          resolve(examples);
        } catch (error) {
          reject(new Error(`Failed to parse CSV: ${error.message}`));
        }
      };
      reader.onerror = () => reject(new Error('Failed to read CSV file'));
      reader.readAsText(file);
    });
  }

  // Save RASA file with new path structure
  async saveRasaFile(userId, flowId, rasaText) {
    try {
      const response = await axios.post(`${this.baseUrl}/rasa`, {
        user_id: userId,
        flow_id: flowId,
        content: rasaText
      });
      return response.data;
    } catch (error) {
      console.error('Error saving RASA file:', error);
      throw new Error(`Failed to save RASA file: ${error.message}`);
    }
  }

  // Transform flow data for backend format (FLOWS directory)
  transformFlowForBackend(flowData) {
    console.log('FileService (Backend): Received canvas data:', flowData);
    if (!flowData || !flowData.nodes) {
      throw new Error('Invalid flow data: Missing nodes structure');
    }

    // Create a map for quick node lookup
    const nodeMap = new Map(flowData.nodes.map(node => [node.id, node]));

    // Helper function to get node label from ID
    const getNodeLabel = (nodeId) => {
      const node = nodeMap.get(nodeId);
      return node ? node.data.label : nodeId;
    };

    // Compose the unified format: nodes as object, edges as array, startNode
    const transformed = {
      nodes: {},
      edges: flowData.edges || [],
      startNode: flowData.startNode ? getNodeLabel(flowData.startNode) : null
    };

    // Process nodes
    flowData.nodes.forEach(node => {
      const label = node.data.label;
      const nodeData = { ...node.data };
      const nodeType = node.type.replace('Node', '');

      // Convert next property to use label instead of ID if it exists
      if (nodeData.next) {
        nodeData.next = getNodeLabel(nodeData.next);
      }

      // Convert condition next properties to use labels
      if (nodeData.conditions) {
        nodeData.conditions = nodeData.conditions.map(condition => ({
          ...condition,
          next: condition.next ? getNodeLabel(condition.next) : null
        }));
      }

      // Add position to node
      const nodeWithPosition = {
        ...nodeData,
        position: node.position
      };

      transformed.nodes[label] = {
        ...nodeWithPosition,
        type: nodeType
      };
    });

    // Update edge source/target to use labels instead of IDs
    if (transformed.edges) {
      transformed.edges = transformed.edges.map(edge => ({
        ...edge,
        source: getNodeLabel(edge.source),
        target: getNodeLabel(edge.target)
      }));
    }

    console.log('FileService (Backend): Final transformed data:', transformed);
    return transformed;
  }

  // Save flow to FLOWS directory only, using the unified format
  async saveFlow(flowName, flowData, canvasData) {
    try {
      const name = flowName.replace(/\s+/g, '_');
      // Transform data for backend (unified format)
      const flowsData = this.transformFlowForBackend(canvasData);
      // Save to FLOWS directory
      console.log(`Saving transformed flow to FLOWS directory...`);
      await axios.post(`${this.baseUrl}/flows`, {
        name,
        data: flowsData
      });
      console.log(`Flow "${flowName}" saved successfully to FLOWS directory!`);
      return { success: true, message: 'Flow saved to FLOWS directory' };
    } catch (error) {
      console.error('Error saving flow:', error);
      throw new Error(`Failed to save flow: ${error.message}`);
    }
  }

  // Delete a flow from both directories
  async deleteFlow(filename) {
    try {
      console.log(`Deleting flow "${filename}" from both directories...`);
      const response = await axios.delete(`${this.baseUrl}/flows/${filename}`);
      console.log(`Flow "${filename}" deleted successfully!`);
      return response.data;
    } catch (error) {
      console.error(`Error deleting flow ${filename}:`, error);

      // Add more detailed error information
      let errorMsg = `Failed to delete flow ${filename}`;

      if (error.response) {
        errorMsg = `Server error ${error.response.status}: ${error.response.data}`
      }

      throw new Error(errorMsg);
    }
  }

  async uploadAudio(file) {
    try {
      await this.ensureServerConnection();

      const formData = new FormData();
      formData.append('audio', file);

      console.log(`Uploading audio file "${file.name}" to AUDIO directory...`);
      console.log('Upload URL:', `${this.baseUrl}/upload-audio`);
      console.log('File details:', {
        name: file.name,
        type: file.type,
        size: file.size
      });

      const response = await axios.post(`${this.baseUrl}/upload-audio`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          console.log(`Upload progress: ${percentCompleted}%`);
        }
      });

      console.log('Audio upload response:', response.data);

      if (!response.data.success) {
        throw new Error(response.data.error || 'Upload failed');
      }

      return response.data;
    } catch (error) {
      console.error('Error uploading audio:', error);

      let errorMsg = 'Failed to upload audio file';
      if (error.response) {
        // Server responded with an error
        const serverError = error.response.data.error || error.response.data;
        errorMsg = `Server error ${error.response.status}: ${serverError}`;
        console.error('Server error details:', error.response.data);
      } else if (error.request) {
        // No response received
        errorMsg = 'Network error: No response from server';
        console.error('No response received. Is the server running?');
        console.error('Request details:', error.request);
      } else {
        // Request setup error
        errorMsg = `Upload error: ${error.message}`;
        console.error('Request setup error:', error);
      }

      throw new Error(errorMsg);
    }
  }

  // Save flow to both flowDB (GUI format) and FLOWS (backend format)
  async saveFlowDual(flowName, guiData, backendData) {
    try {
      const name = flowName.replace(/\s+/g, '_');
      // Save GUI/canvas data to flowDB
      console.log(`Saving GUI flow to flowDB directory...`);
      await axios.post(`${this.baseUrl}/flowsDB`, {
        name,
        data: guiData
      });
      // Save backend/clean data to FLOWS
      console.log(`Saving backend flow to FLOWS directory...`);
      await axios.post(`${this.baseUrl}/flows`, {
        name,
        data: backendData
      });
      console.log(`Flow "${flowName}" saved successfully to both flowDB and FLOWS!`);
      return { success: true, message: 'Flow saved to both flowDB and FLOWS' };
    } catch (error) {
      console.error('Error saving flow:', error);
      throw new Error(`Failed to save flow: ${error.message}`);
    }
  }

  // Get list of available flows from flowDB directory (GUI format)
  async getFlowsDB() {
    try {
      await this.ensureServerConnection();
      console.log('Fetching flows from flowDB directory:', `${this.baseUrl}/flowDB`);
      const response = await axios.get(`${this.baseUrl}/flowDB`);
      console.log('flowDB response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching flowDB:', error);
      let errorMsg = 'Failed to fetch flows from flowDB';
      if (error.response) {
        errorMsg = `Server error ${error.response.status}: ${error.response.data}`;
      } else if (error.request) {
        errorMsg = `Network error: No response from server. Is the server running at ${this.baseUrl.replace('/api', '')}?`;
        console.warn('Try starting the server with: node backend/server.js');
      } else {
        errorMsg = `Request error: ${error.message}`;
      }
      throw new Error(errorMsg);
    }
  }

  // Get a specific flow by filename from flowDB directory (GUI format)
  async getFlowDB(userId, filename) {
    try {
      console.log('Fetching flow from flowDB directory:', filename);
      const response = await axios.get(`${this.baseUrl}/flowDB/${userId}/${filename}`);
      console.log('flowDB data retrieved successfully');
      return response.data;
    } catch (error) {
      console.error(`Error fetching flowDB ${filename}:`, error);
      let errorMsg = `Failed to fetch flow ${filename} from flowDB`;
      if (error.response) {
        errorMsg = `Server error ${error.response.status}: ${error.response.data}`;
      } else if (error.request) {
        errorMsg = `Network error: No response from server`;
      } else {
        errorMsg = `Request error: ${error.message}`;
      }
      throw new Error(errorMsg);
    }
  }

  // Get a specific flow by filename from flowDB/{user_id}/{filename} (GUI format)
  async getUserFlow(userId, filename) {
    try {
      console.log(`Fetching flow from flowDB/${userId}/${filename}`);
      const response = await axios.get(`${this.baseUrl}/flowDB/${userId}/${filename}`);
      console.log('User flow data retrieved successfully');
      return response.data;
    } catch (error) {
      console.error(`Error fetching user flow ${filename}:`, error);
      let errorMsg = `Failed to fetch user flow ${filename}`;
      if (error.response) {
        const serverError = typeof error.response.data === 'object'
          ? JSON.stringify(error.response.data)
          : error.response.data;
        errorMsg = `Server error ${error.response.status}: ${serverError}`;
      } else if (error.request) {
        errorMsg = `Network error: No response from server`;
      } else {
        errorMsg = `Request error: ${error.message}`;
      }
      throw new Error(errorMsg);
    }
  }

  // Check if flow name exists for a user
  async checkFlowNameExists(userId, flowName) {
    try {
      const response = await axios.get(`${this.baseUrl}/flows/${userId}/check-name/${encodeURIComponent(flowName)}`);
      return response.data.exists;
    } catch (error) {
      console.error('Error checking flow name:', error);
      throw new Error(`Failed to check flow name: ${error.message}`);
    }
  }

  // Get RASA server status
  async getRasaServerStatus() {
    try {
      const response = await axios.get(`${this.baseUrl}/rasa/status`);
      return response.data;
    } catch (error) {
      console.error('Error getting RASA server status:', error);
      throw new Error(`Failed to get RASA server status: ${error.message}`);
    }
  }

  // Stop RASA server
  async stopRasaServer(port) {
    try {
      const response = await axios.post(`${this.baseUrl}/rasa/stop/${port}`);
      return response.data;
    } catch (error) {
      console.error('Error stopping RASA server:', error);
      throw new Error(`Failed to stop RASA server: ${error.message}`);
    }
  }
}

const fileService = new FileService();
export default fileService;



